import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Briefcase, Building2, X, Command } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ creators: [], campaigns: [], brands: [] });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Open on ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
      setQ('');
      setResults({ creators: [], campaigns: [], brands: [] });
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!q.trim()) {
      setResults({ creators: [], campaigns: [], brands: [] });
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/search?q=${encodeURIComponent(q)}`, { withCredentials: true });
        setResults(data || { creators: [], campaigns: [], brands: [] });
        setActiveIdx(0);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const flat = [
    ...results.creators.map((r) => ({ ...r, _type: 'creator' })),
    ...results.campaigns.map((r) => ({ ...r, _type: 'campaign' })),
    ...results.brands.map((r) => ({ ...r, _type: 'brand' })),
  ];

  const navigateTo = (item) => {
    setOpen(false);
    if (item._type === 'creator') navigate(`/creators/${item.id}`);
    else if (item._type === 'campaign') {
      if (item.type === 'brand_brief') navigate('/brand/campaigns');
      else navigate(`/campaigns/${item.id}/workspace`);
    } else if (item._type === 'brand') navigate('/admin/platform');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && flat[activeIdx]) { e.preventDefault(); navigateTo(flat[activeIdx]); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-24 px-4 bg-black/40 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="command-palette-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-[#0F0F0F]/95 backdrop-blur-2xl rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.7),0_0_60px_rgba(255,69,26,0.15)] border border-[#FF451A]/30 overflow-hidden"
        data-testid="command-palette"
      >
        <div className="flex items-center gap-3 px-4 border-b border-black/[0.08] dark:border-white/5 bg-gradient-to-b from-white/[0.03] to-transparent">
          <Search size={18} className="text-[#FF451A]" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search creators, campaigns, brands…"
            className="flex-1 py-4 outline-none text-[#0F0F0F] dark:text-white placeholder:text-zinc-500 bg-transparent font-body"
            data-testid="command-palette-input"
          />
          <kbd className="font-mono text-[10px] uppercase font-semibold text-[#6B6B6B] dark:text-zinc-400 bg-white/5 border border-black/[0.10] dark:border-white/10 px-1.5 py-0.5 rounded">ESC</kbd>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {!q.trim() ? (
            <div className="p-8 text-center text-sm text-[#6B6B6B] dark:text-zinc-500">
              <Command className="mx-auto mb-2 opacity-60 text-[#FF451A]" size={20} />
              Type to search across the platform
            </div>
          ) : loading ? (
            <div className="p-6 text-center text-sm text-[#6B6B6B] dark:text-zinc-500">Searching…</div>
          ) : flat.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#6B6B6B] dark:text-zinc-500">No results for "{q}"</div>
          ) : (
            <>
              {results.creators.length > 0 && (
                <Section title="Creators">
                  {results.creators.map((c, i) => {
                    const idx = i;
                    return (
                      <Row
                        key={c.id}
                        active={activeIdx === idx}
                        onClick={() => navigateTo({ ...c, _type: 'creator' })}
                        icon={<Users size={14} className="text-[#FF451A]" />}
                        title={c.name}
                        subtitle={`@${c.instagram_handle || '—'} · ${fmt(c.followers)} followers · Grade ${c.quality_grade}`}
                        tag={c.exclusivity_type}
                      />
                    );
                  })}
                </Section>
              )}
              {results.campaigns.length > 0 && (
                <Section title="Campaigns">
                  {results.campaigns.map((c, i) => {
                    const idx = results.creators.length + i;
                    return (
                      <Row
                        key={c.id}
                        active={activeIdx === idx}
                        onClick={() => navigateTo({ ...c, _type: 'campaign' })}
                        icon={<Briefcase size={14} className="text-[#0055FF]" />}
                        title={c.name}
                        subtitle={`${c.brand || ''} · ${c.stage || c.type}`}
                      />
                    );
                  })}
                </Section>
              )}
              {results.brands.length > 0 && (
                <Section title="Brands">
                  {results.brands.map((b, i) => {
                    const idx = results.creators.length + results.campaigns.length + i;
                    return (
                      <Row
                        key={b.id}
                        active={activeIdx === idx}
                        onClick={() => navigateTo({ ...b, _type: 'brand' })}
                        icon={<Building2 size={14} className="text-[#00C972]" />}
                        title={b.name}
                        subtitle={b.industry}
                      />
                    );
                  })}
                </Section>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-t border-black/[0.08] dark:border-white/5 bg-black/40 text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono uppercase tracking-wider">
          <span><kbd className="px-1 py-0.5 bg-white/5 border border-black/[0.10] dark:border-white/10 rounded">↑</kbd> <kbd className="px-1 py-0.5 bg-white/5 border border-black/[0.10] dark:border-white/10 rounded">↓</kbd> navigate</span>
          <span><kbd className="px-1 py-0.5 bg-white/5 border border-black/[0.10] dark:border-white/10 rounded">⏎</kbd> open</span>
          <span><kbd className="px-1 py-0.5 bg-white/5 border border-black/[0.10] dark:border-white/10 rounded">⌘K</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="px-4 pt-3 pb-1 text-[10px] uppercase font-semibold tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 font-mono">{title}</div>
    {children}
  </div>
);

const Row = ({ active, onClick, icon, title, subtitle, tag }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${active ? 'bg-[#FF451A]/15 border-l-2 border-[#FF451A]' : 'hover:bg-white/[0.04] border-l-2 border-transparent'}`}
    data-testid="command-palette-row"
  >
    <div className="w-7 h-7 rounded-lg bg-white/5 border border-black/[0.10] dark:border-white/10 flex items-center justify-center shrink-0">{icon}</div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-[#0F0F0F] dark:text-white truncate">{title}</div>
      {subtitle && <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 truncate">{subtitle}</div>}
    </div>
    {tag && <span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400 px-1.5 py-0.5 bg-white/5 border border-black/[0.10] dark:border-white/10 rounded">{tag}</span>}
  </button>
);

const fmt = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n);
